import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useCallback, useState } from 'react';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import TemplateSelector from 'components/field/TemplateSelector';
import { Form } from 'components/form/FormLayout.styled';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import * as S from 'pages/Settings/Print/Print.styled';

import AppLogger from 'utils/AppLogger';

import { faFloppyDisk, faXmark } from '@fortawesome/pro-solid-svg-icons';

const TemplateSelectionForm = NiceModal.create(
	({ setting, templateName, handleReset, formMethods, onSubmit, handleTemplateChange }) => {
		const { t } = useTranslation();
		const modal = useModal();
		const [selectedTemplate, setSelectedTemplate] = useState(templateName);

		const { handleSubmit, setValue, control } = formMethods;

		const { isDirty, isSubmitting } = useFormState({ control });

		const onChange = useCallback(
			(template) => {
				setSelectedTemplate(template.name);
				handleTemplateChange(template);
				setValue(`${setting.formFieldName}.templateName`, template.name, {
					shouldValidate: true,
					shouldDirty: true
				});
			},
			[setting]
		);

		const handleCloseModal = useCallback(() => {
			if (isDirty) {
				NiceModal.show(ConfirmationModal, {
					title: 'youHaveUnsavedChanges',
					subTitle: 'areYouSureYouWantToLeave',
					yesAction: () => {
						handleReset();
						setSelectedTemplate(templateName);
						modal.remove();
					}
				});
			} else {
				modal.remove();
			}
		}, [isDirty]);

		const submitName = useCallback(() => {
			onSubmit();
			modal.remove();
		}, []);

		return (
			<>
				<Modal maxWidth="medium" className="modal-printTemplateSelection" closeButton={false}>
					<ModalTitle
						style={{
							display: 'flex',
							justifyContent: 'space-between'
						}}>
						<Typography fontWeight="500" variant="p1">
							{t('print.changeTemplate')} - {t(setting.label)}
						</Typography>
						<Button
							className="closeBtn"
							variant="borderless"
							icon={faXmark}
							onClick={handleCloseModal}
							iconSize="medium"
							iconButton
						/>
					</ModalTitle>
					<ModalContent>
						<Form formMethods={formMethods}>
							<S.TemplateCardContainer>
								{setting.templates.map((template) => (
									<S.TemplateCardItem
										key={`${setting.label}-${template.name}`}
										name={`${setting.label}-${template.name}`}>
										<TemplateSelector
											name={template.name}
											checkAction={() => onChange(template)}
											imageUrl={t(template.image)}
											className={selectedTemplate === template.name ? 'selected' : ''}
										/>
									</S.TemplateCardItem>
								))}
							</S.TemplateCardContainer>
						</Form>
					</ModalContent>
					<ModalActions>
						<Button label="cancel" variant="outline" onClick={() => handleCloseModal()} />
						<Button
							className="saveButton"
							onClick={handleSubmit(submitName, (errors) => {
								AppLogger.log(errors);
							})}
							icon={faFloppyDisk}
							label="save"
							disabled={!isDirty || isSubmitting}
							disabledTooltip={!isDirty && 'formInfo.noChanges'}
						/>
					</ModalActions>
				</Modal>
			</>
		);
	}
);

export default TemplateSelectionForm;
